import React, { useState, useEffect } from 'react';
import { Box, Container, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const HeroSection = styled(Box)`
  height: 100vh;
  width: 100%;
  max-width: 100vw;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #000B18 0%, #001529 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    height: 100vh;
    padding: 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, rgba(0, 229, 255, 0.15) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const BackgroundPattern = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(1000px) rotateX(60deg) scale(6);
  transform-origin: center 0;
  opacity: 0.4;
  z-index: 1;
`;

const EdgeGlow = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, transparent 0%, rgba(0, 229, 255, 0.1) 50%, transparent 100%),
    linear-gradient(180deg, transparent 0%, rgba(0, 229, 255, 0.1) 50%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

const RadialGlow = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(0, 229, 255, 0.15) 0%, transparent 50%);
  opacity: 0.5;
  z-index: 1;
`;

const GlowingOrb = styled(motion.div)`
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(0, 229, 255, 0.1) 0%, transparent 70%);
  filter: blur(60px);
  opacity: 0.5;
  pointer-events: none;
`;

const TopGlow = styled(GlowingOrb)`
  top: -300px;
  right: -200px;
  background: radial-gradient(circle at center, rgba(0, 229, 255, 0.15) 0%, transparent 70%);
`;

const BottomGlow = styled(GlowingOrb)`
  bottom: -300px;
  left: -200px;
  background: radial-gradient(circle at center, rgba(41, 121, 255, 0.1) 0%, transparent 70%);
`;

const ContentContainer = styled(Container)`
  width: 100%;
  max-width: 100%;
  z-index: 2;
  position: relative;
  padding: 0;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeroContent = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
`;

const TextContent = styled(Box)`
  max-width: 1000px;
  text-align: center;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Title = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 800;
  color: white;
  margin: 0 auto;
  line-height: 1.1;
  letter-spacing: -1px;
  position: relative;
  text-align: center;
  
  .main-title {
    display: block;
    background: linear-gradient(90deg, #00e5ff 0%, #2979ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
    text-shadow: 0 0 30px rgba(0, 229, 255, 0.3);
    font-size: 4.5rem;
    margin-bottom: 0.2em;
  }
  
  .highlight {
    background: linear-gradient(90deg, #00e5ff 0%, #2979ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 0 30px rgba(0, 229, 255, 0.3);
  }
  
  @media (max-width: 1200px) {
    font-size: 4rem;
    .main-title {
      font-size: 4rem;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    .main-title {
      font-size: 3rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 2.5rem;
    .main-title {
      font-size: 2.5rem;
    }
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 1rem auto 2rem;
  line-height: 1.6;
  max-width: 750px;
  font-weight: 300;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 1rem auto 1.5rem;
  }
`;

const WhatsAppLink = styled.a`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #00e5ff;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin: 1.5rem auto 0;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
    max-width: 300px;
  }
`;

const StyledButton = styled(motion.button)`
  padding: 1.2rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  outline: none;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 1rem 2.5rem;
    font-size: 1rem;
  }
`;

const CTAButton = styled(StyledButton)`
  background: linear-gradient(45deg, #25D366 0%, #128C7E 100%);
  color: white;
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.2);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    box-shadow: 0 15px 40px rgba(37, 211, 102, 0.3);
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled(StyledButton)`
  background: rgba(255, 255, 255, 0.03);
  color: white;
  border: 1px solid rgba(0, 229, 255, 0.3);
  
  &:hover {
    border-color: rgba(0, 229, 255, 0.8);
    background: rgba(0, 229, 255, 0.05);
    transform: translateY(-2px);
  }
`;

const IllustrationContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 800px;
  height: 500px;
  perspective: 1000px;
  margin: 2rem auto 0;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: -50px;
    right: -50px;
    bottom: -50px;
    background: radial-gradient(circle at center, rgba(0, 229, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }
  
  @media (max-width: 1200px) {
    max-width: 700px;
    height: 450px;
  }
  
  @media (max-width: 768px) {
    max-width: 500px;
    height: 400px;
  }
`;

const CircleBackground = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(0, 229, 255, 0.15) 0%, transparent 70%);
  filter: blur(60px);
  transform-style: preserve-3d;
  opacity: 0.8;
`;

const GridPattern = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.1) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  border-radius: 50%;
  transform: rotate(-15deg);
  transform-style: preserve-3d;
  box-shadow: 0 0 50px rgba(0, 229, 255, 0.1);
  opacity: 0.6;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(0, 229, 255, 0.1) 0%, transparent 100%);
    border-radius: 50%;
  }
`;

const FloatingCube = styled(motion.div)`
  position: absolute;
  width: 100px;
  height: 100px;
  background: rgba(0, 229, 255, 0.1);
  border: 1px solid rgba(0, 229, 255, 0.3);
  transform-style: preserve-3d;
  backdrop-filter: blur(5px);
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  z-index: 10;
  padding: 10px 0;
  width: 100%;
  text-align: center;
  background: linear-gradient(to bottom, transparent 0%, rgba(0, 11, 24, 0.9) 100%);

  &:hover {
    color: #4facfe;
  }

  span {
    display: block;
    text-align: center;
    width: 100%;
  }

  svg {
    font-size: 1.25rem;
  }
`;

const TypingText = styled.span`
  color: #4facfe;
  position: relative;
  
  &::after {
    content: '|';
    position: absolute;
    right: -8px;
    animation: blink 1s step-end infinite;
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const MobileContainer = styled(Box)`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    padding: 2rem 1rem;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
`;

const Hero = () => {
  const [typingText, setTypingText] = useState('Intelligent');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const words = ['Intelligent', 'Strategic', 'Adaptive', 'Innovative', 'Precision'];
  const period = 2000;
  const [delta, setDelta] = useState(100);

  useEffect(() => {
    let ticker = setInterval(() => {
      tick();
    }, delta);

    return () => clearInterval(ticker);
  }, [typingText, isDeleting]);

  const tick = () => {
    let i = loopNum % words.length;
    let fullText = words[i];
    let updatedText = isDeleting 
      ? fullText.substring(0, typingText.length - 1)
      : fullText.substring(0, typingText.length + 1);

    setTypingText(updatedText);

    if (isDeleting) {
      setDelta(prevDelta => prevDelta / 2);
    }

    if (!isDeleting && updatedText === fullText) {
      setIsDeleting(true);
      setDelta(period);
    } else if (isDeleting && updatedText === '') {
      setIsDeleting(false);
      setLoopNum(loopNum + 1);
      setDelta(100);
    }
  };

  const handleScroll = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="home">
      <BackgroundPattern />
      <EdgeGlow />
      <RadialGlow
        animate={{
          opacity: [0.3, 0.5, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
      />
      <TopGlow />
      <BottomGlow />
      <ContentContainer maxWidth="xl">
        <HeroContent container>
          <TextContent>
            <Title
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span className="main-title">Transforming</span>
              Enterprises Through <TypingText>{typingText}</TypingText> Solutions
            </Title>
            <Subtitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Where cutting-edge AI meets strategic consulting to create unprecedented business value and competitive advantage.
            </Subtitle>
            <ButtonGroup
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <CTAButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open('https://wa.me/918756992565', '_blank')}
              >
                <WhatsAppIcon /> Connect on WhatsApp
              </CTAButton>
            </ButtonGroup>
          </TextContent>
        </HeroContent>
      </ContentContainer>
      
      <ScrollIndicator
        onClick={handleScroll}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <span>Discover More</span>
        <KeyboardArrowDownIcon />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero; 